import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import PaymentTypeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/payment-masters/payment-type/payment-type.entity";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { IAPIListResult, IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { andWhere, buildQuery, FilterFn, Ops, orderBy, OrderVariant, orWhere, page, where, select, noop } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { ICreatePaymentType } from "./interfaces/create-payment-type.interface";
import { IUpdatePaymentType } from "./interfaces/update-payment-type.interface";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { IFilters } from "../../filters/interfaces/filterInterface";


const apiUrl = env.API_PAYMENT_MASTER_URL;
const moduleName = "payment_type";

@Injectable({
  providedIn: "root",
})
export class PaymentTypeService extends AbstractService<PaymentTypeAttributes, ICreatePaymentType, IUpdatePaymentType> {
    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Obtiene tipos de pago con paginación y filtros.
     *
     * Estrategia de búsqueda:
     * - Sin búsqueda: Un solo objeto con filtros base
     * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
     *
     * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
     *
     * @param options Pagination options
     * @param dataToFilter Filters to apply
     * @returns Paginated data
     *
     * Search fields: code, name
     * Array filters: paymentMethodId, status
     * Single filters: companyId
     */
    getAllPaginatePaymentType(
        options: IPagination<PaymentTypeAttributes>,
        dataToFilter?: IFilters
    ): Observable<IPaginationResult<PaymentTypeAttributes>> {

        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, filtro de compañía, método pago, estados.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<PaymentTypeAttributes>[] => {
            const deletedFilter: FilterFn<PaymentTypeAttributes> = params.useOrWhere
                ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
                : where('_deleted', Ops.eq(false));   // Primer objeto del array

            return [
                deletedFilter,
                dataToFilter?.companyId
                    ? andWhere('company.id' as keyof PaymentTypeAttributes, Ops.eq(dataToFilter.companyId))
                    : noop(),
                dataToFilter?.paymentMethodId && dataToFilter.paymentMethodId.length > 0
                    ? andWhere('paymentMethodCode' as keyof PaymentTypeAttributes, Ops.in(...dataToFilter.paymentMethodId))
                    : noop(),
                dataToFilter?.status && dataToFilter.status.length > 0
                    ? andWhere('statusId', Ops.in(...dataToFilter.status))
                    : noop()
            ];
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
        const searchByCodeQueryObject: FilterFn<PaymentTypeAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
            hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
        const searchByNameQueryObject: FilterFn<PaymentTypeAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
            andWhere("name", Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<PaymentTypeAttributes>(
            // Expandimos ambos objetos en el buildQuery
            ...searchByCodeQueryObject,
            ...searchByNameQueryObject,

            // Ordenamiento y paginación
            orderBy(
                options.sort ?? "name",
                options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string = apiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<PaymentTypeAttributes>>(API_URL);
    }

    /**
   * Find to combo box
   * @returns Data
   */
    findToComboBox(
      method?: string[]
    ): Observable<IRequestResult<PaymentTypeAttributes[]>> {
        const query = buildQuery<PaymentTypeAttributes>(
          where("statusId", StatusEnum.ACTIVE),
          method ? andWhere(
            "paymentMethodCode" as keyof PaymentTypeAttributes,
            Ops.in(...method)
          ): noop(),
          select(["_id","name", "code", "externalKeys",'paymentMethodCode']),
          orderBy("code", OrderVariant.Asc)
        );
    
        const API_URL: string =
          apiUrl +
          `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IRequestResult<PaymentTypeAttributes[]>>(API_URL);
      }
}
